import React, { useCallback, useRef, useState } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid';
import { withLocalePrefix } from 'shared/utils/translation';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import {
  Body,
  Label,
  SecondaryBody,
  SectionTitle,
} from '@grnhse/seedling/lib/birch/typography';
import CheckboxField from '@grnhse/seedling/lib/birch/components/forms/fields/checkbox_field';
import SkeletonLoader from '@grnhse/seedling/lib/birch/components/skeleton_loader';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import {
  DangerTertiaryButton,
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { ThemeableModal } from '@grnhse/seedling/lib/birch/components/modals';
import {
  ExpandableMenu,
  MenuList,
  MenuItem,
  TransparentMenuButton,
} from '@grnhse/seedling/lib/birch/components/expandable_menu';
import {
  MountingMode,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@grnhse/seedling/lib/birch/components/tabs/';
import {
  useCalibrationSettings,
  useGenerateCalibrationFromText,
  useGenerateSkillsDescription,
  useSetCalibrationSettingsData,
  useTopSkills,
} from 'talent_matching/api';
import { useRankingInProgress } from 'talent_matching/contexts/ranking_in_progress_context';
import SliderComponent from './slider';
import {
  Skill,
  CalibrationSettings,
  SettingsFormData,
  SkillValidationError,
} from 'talent_matching/types';
import Container from '@grnhse/seedling/lib/birch/components/container';
import { EMPTY_FORM_STATE } from 'talent_matching/components/calibration/calibration_modal';
import UserPrompt from 'talent_matching/components/calibration/user_prompt';
import { useCalibrationModal } from 'talent_matching/contexts/calibration_modal_context';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import styled from 'styled-components';
import uniqBy from 'lodash/uniqBy';
import clamp from 'lodash/clamp';
import { Tooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import LoadingSpinner from '@grnhse/seedling/lib/birch/components/loading_spinner';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import AddInCircleIcon from '@grnhse/seedling/lib/birch/icons/add_in_circle';
import ArrowDownIcon from '@grnhse/seedling/lib/birch/icons/arrow_down';
import AiCallout from 'shared/components/ai_callout';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import DeleteConfirmationModal from 'talent_matching/components/calibration/delete_confirmation_modal';
import HistoryCalibrationTab from './history_calibration_tab';
import ConfirmationModal from '@grnhse/seedling/lib/birch/components/modals/confirmation_modal';
import FingerprintOne from '@grnhse/seedling/lib/birch/illustrations/fingerprint_one';
import CalibrationSkillsPanel from './calibration_form/calibration_skills_panel';
import CalibrationDetailsPanel from './calibration_form/calibration_details_panel';
import { useCalibrationHistoryState } from './calibration_form/use_calibration_history_state';
import { useExitWithoutSaving } from './calibration_form/use_exit_without_saving';
import { useCalibrationFormStateSync } from './calibration_form/use_calibration_form_state_sync';

const t = withLocalePrefix('talent_matching.modals.calibration');
const CalibrationModalBody = styled(Grid)`
  min-height: 0;
  overflow: auto;
  padding-top: 12px;
  padding-bottom: 0 !important;
  display: flex;
  flex-direction: column;
`;

const FullHeightTabs = styled(Tabs)`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;

  [data-reach-tab-panels] {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding-bottom: 0;
  }

  [data-reach-tab-panel] {
    flex: 1 1 auto;
    min-height: 0;
  }

  [data-reach-tab-panel]:not([hidden]) {
    display: flex;
    flex-direction: column;
  }

  [data-reach-tab-panel][hidden] {
    display: none;
  }
`;

const InlineGenerateButton = styled(SecondaryButton)`
  align-self: flex-start;
  width: auto !important;
  min-width: 200px;
`;
const InlineClearButton = styled(TertiaryButton)`
  align-self: flex-start;
  padding-left: 0;
  padding-right: 0;
  margin-left: 0;
`;
const InlineButtonWrapper = styled.div`
  display: inline-flex;
`;
const GenerationDivider = styled.div`
  border-top: 1px solid ${FullPalette.GREY_600};
  width: 100%;
  margin-top: ${Tokens.spacing['1']};
  padding-top: ${Tokens.spacing['1']};
`;

const TabListWrapper = styled.div`
  margin-top: -25px;
  margin-bottom: -17px;

  div[role='tablist'] {
    column-gap: 14px;
  }
`;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: SettingsFormData) => void;
  validationErrors?: SkillValidationError[];
  isValidating?: boolean;
};

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 0.3fr auto 0.7fr;
  gap: ${Tokens.spacing['3']};
  align-items: stretch;
  flex: 1 1 auto;
  min-height: 0;
`;

const ColumnsFrame = styled.div`
  border-top: 1px solid ${FullPalette.GREY_600};
  margin: 0 -${Tokens.spacing['3']};
  padding: 0 ${Tokens.spacing['3']};
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const FooterBorderWrapper = styled.div`
  border-top: 1px solid ${(props) => props.theme.borderColor};
`;

const ColumnDivider = styled.div`
  width: 1px;
  background-color: ${FullPalette.GREY_600};
  align-self: stretch;
  justify-self: center;
  height: 100%;
`;

const AssistantCard = styled(Container)`
  background-color: ${FullPalette.WHITE};
  border: none;
  box-shadow: none;
  padding-left: 0;
  padding-right: 0;
`;

const RightColumn = styled(Stack)`
  padding-top: ${Tokens.spacing['2']};
`;

const AssistantTitle = styled(Body)`
  color: ${FullPalette.EVERGREEN_700};
  font-weight: 500;
  line-height: 1.5rem;
`;

const SuggestedPromptMenuWrapper = styled.div`
  align-self: flex-start;
  display: inline-flex;
`;

const SuggestedPromptButton = styled(TransparentMenuButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${Tokens.spacing['0.5']};
  padding: ${Tokens.spacing['0.25']} 0;
  font-family: 'Untitled Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${FullPalette.GREEN_700};
  text-align: center;
  text-decoration: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;

  svg {
    flex-shrink: 0;
  }

  &:hover,
  &:focus-visible {
    color: ${FullPalette.EVERGREEN_700};
    text-decoration: underline;
  }

  &[aria-disabled='true'],
  &:disabled {
    color: ${FullPalette.GREY_600};
    cursor: not-allowed;
    text-decoration: none;
    outline: none;
  }
`;

const ErrorText = styled(SecondaryBody)`
  color: ${Tokens.colors.error};
`;

const GenerationNotice = styled(SecondaryBody)`
  font-size: 12px;
  line-height: 16px;
`;

const EmptyHistoryContainer = styled(Stack)`
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 640px;
  padding: ${Tokens.spacing['4']};
  gap: ${Tokens.spacing['2']};
`;

const EmptyTitle = styled(SecondaryBody)`
  color: ${FullPalette.GREY_800};
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
`;

const EmptySubtitle = styled(SecondaryBody)`
  color: ${FullPalette.GREY_800};
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
`;

const EmptyIllustration = styled(FingerprintOne)`
  width: 96px;
  height: 96px;
  opacity: 0.6;
`;

const CalibrationForm = ({
  isOpen,
  onClose,
  onSubmit,
  validationErrors = [],
  isValidating = false,
}: Props) => {
  const {
    paths,
    canCreateCalibrationSetting,
    canEditAndDeleteTalentMatchingCalibration,
  } = useCalibrationModal();
  const popperRef = useRef<HTMLElement | null>(null);

  const {
    watch,
    setValue,
    reset,
    resetField,
    setError,
    clearErrors,
    formState: { errors, isDirty },
  } = useFormContext();
  const [
    devOnlyAllApps,
    minYearsExperience,
    prioritizeCurrentExperience,
    industries,
    skills,
    includeSimilarRoles,
    userPrompt,
    userPromptMode,
    jobTitles,
    includeJobDescription,
    includeScorecardAttributes,
  ] = watch([
    'dev_only_all_apps',
    'min_years_experience',
    'prioritize_current_experience',
    'industries',
    'skills',
    'use_boolean_search',
    'userPrompt',
    'userPromptMode',
    'job_titles',
    'include_job_description',
    'include_scorecard_attributes',
  ]);

  const {
    data: calibrationSettingsResponse,
    isLoading: isLoadingSettings,
    isSuccess: isSettingsSuccess,
  } = useCalibrationSettings(paths);
  const { rankingInProgress, hasCalibrationSetting } = useRankingInProgress();
  const [isDeleteModalOpen, openDeleteModal, closeDeleteModal] = useModal();

  const { mutate: generateCalibrationFromText, isPending: isGenerating } =
    useGenerateCalibrationFromText(paths);
  const { setCalibrationSettingsData } = useSetCalibrationSettingsData(paths);

  const settings = calibrationSettingsResponse?.settings;
  const hasJobDescription =
    calibrationSettingsResponse?.has_job_description ?? true;
  const hasScorecardAttributes =
    calibrationSettingsResponse?.has_scorecard_attributes ?? true;

  const [suggestedPrompt, setSuggestedPrompt] = useState<string | null>(null);
  const [suggestedPromptError, setSuggestedPromptError] = useState<
    string | null
  >(null);
  const [activeTab, setActiveTab] = useState<'current' | 'history'>('current');
  const isHistoryTab = activeTab === 'history';
  const isCurrentCalibrationTab = activeTab === 'current';

  const buildFormValuesFromCalibration = useCallback(
    (calibration: Partial<CalibrationSettings>): Partial<SettingsFormData> => ({
      job_titles: calibration.job_titles || [],
      min_years_experience: calibration.min_years_experience ?? '',
      use_boolean_search: calibration.use_boolean_search || false,
      prioritize_current_experience:
        calibration.prioritize_current_experience || false,
      skills: calibration.skills || [],
      industries: calibration.industries || [],
    }),
    []
  );

  const {
    data: fetchedSuggestedPrompt,
    refetch: fetchSuggestedPrompt,
    isFetching: isFetchingSuggestedPrompt,
  } = useGenerateSkillsDescription(paths, false);

  const settingsExist = !!settings && Object.keys(settings || {}).length > 0;
  const hasExistingCalibration = hasCalibrationSetting || settingsExist;

  // We only want to fetch top skills when the modal is open
  const shouldFetchTopSkills = isOpen && isSettingsSuccess;

  const {
    data: initialSkills,
    isLoading: isLoadingSkills,
    error: topSkillsError,
  } = useTopSkills(paths, shouldFetchTopSkills, 5, false);
  const suggestedSkillsError = topSkillsError
    ? t('calibrate_skills.suggested_skills_error')
    : undefined;

  const isLoading = isLoadingSettings || isLoadingSkills || isGenerating;
  const applyingHistoryRef = useRef(false);

  const {
    historyItems,
    isHistoryLoading,
    historyError,
    selectedHistory,
    selectedHistoryId,
    setSelectedHistoryId,
    isUseVersionModalOpen,
    handleUseVersionClick,
    dismissUseVersionModal,
    applyHistoryEntryToCurrent,
  } = useCalibrationHistoryState({
    paths,
    isOpen,
    isHistoryTab,
    buildFormValuesFromCalibration,
    reset,
    setActiveTab,
    applyingHistoryRef,
  });

  useCalibrationFormStateSync({
    isOpen,
    isHistoryTab,
    selectedHistory,
    settingsExist,
    settings,
    setValue,
    reset,
    buildFormValuesFromCalibration,
    hasJobDescription,
    hasScorecardAttributes,
    initialSkills,
    fetchedSuggestedPrompt,
    setSuggestedPrompt,
    setSuggestedPromptError,
    applyingHistoryRef,
  });

  const {
    isExitWithoutSavingModalOpen,
    handleOnClose,
    closeExitWithoutSavingModal,
    closeAndReset,
  } = useExitWithoutSaving({
    isDirty,
    onClose,
    setValue,
    clearErrors,
    setActiveTab,
    userPromptMode,
  });

  const onGenerateCalibration = async () => {
    if (!userPrompt && !includeJobDescription && !includeScorecardAttributes) {
      return;
    }

    clearErrors('root.parseSkills');
    resetField('userPrompt', { defaultValue: userPrompt });
    const includeJobDescriptionCurrent =
      includeJobDescription && hasJobDescription;
    const includeScorecardAttributesCurrent =
      includeScorecardAttributes && hasScorecardAttributes;

    generateCalibrationFromText(
      {
        user_prompt: userPrompt,
        include_job_description: includeJobDescriptionCurrent,
        include_scorecard_attributes: includeScorecardAttributesCurrent,
      },
      {
        onSuccess: (calibrationData) => {
          const incomingTitles = (calibrationData?.calibration_settings
            ?.job_titles ?? []) as string[];
          const currentTitles = jobTitles as string[];

          const mergedTitles = uniqBy(
            [...currentTitles, ...incomingTitles],
            (s: string) => (s || '').trim().toLowerCase()
          )
            .map((s: string) => (s || '').trim())
            .filter((s: string) => s.length > 0);

          const incomingSkills = (calibrationData?.calibration_settings
            ?.skills ?? []) as Skill[];
          const currentSkills = skills as Skill[];

          const mergedSkills = uniqBy(
            [...currentSkills, ...incomingSkills],
            (s: Skill) => (s.name || '').trim().toLowerCase()
          );

          const nextCalibrationSettings: CalibrationSettings = {
            ...calibrationData.calibration_settings,
            skills: mergedSkills,
            job_titles: mergedTitles,
            userPrompt,
            include_job_description: includeJobDescriptionCurrent,
            include_scorecard_attributes: includeScorecardAttributesCurrent,
          };

          setCalibrationSettingsData({
            ...nextCalibrationSettings,
          });

          setValue('skills', nextCalibrationSettings.skills ?? [], {
            shouldDirty: true,
          });
          setValue('job_titles', mergedTitles, { shouldDirty: true });
          setValue(
            'min_years_experience',
            nextCalibrationSettings.min_years_experience ?? '',
            {
              shouldDirty: true,
            }
          );
          setValue(
            'use_boolean_search',
            nextCalibrationSettings.use_boolean_search ?? false,
            { shouldDirty: true }
          );
          setValue(
            'prioritize_current_experience',
            nextCalibrationSettings.prioritize_current_experience ?? false,
            { shouldDirty: true }
          );
          setValue('industries', nextCalibrationSettings.industries ?? [], {
            shouldDirty: true,
          });
          setValue('skills', mergedSkills, { shouldDirty: true });
          setValue('include_job_description', includeJobDescriptionCurrent, {
            shouldDirty: true,
          });
          setValue(
            'include_scorecard_attributes',
            includeScorecardAttributesCurrent,
            { shouldDirty: true }
          );
          setValue(
            'extracted_skills',
            nextCalibrationSettings.extracted_skills,
            { shouldDirty: false }
          );
          setValue('userPromptMode', 'view');
          clearErrors('root.parseSkills');
        },
        onError: (error) => {
          // eslint-disable-next-line no-console
          console.error('Error parsing skills:', error);
          setError('root.parseSkills', {
            type: 'server',
            message: t('ai_input_section.parse_error'),
          });
        },
      }
    );
  };

  const onClear = () => {
    clearErrors('root.parseSkills');
    clearErrors('root.suggestedSkills');
    reset({ ...EMPTY_FORM_STATE });
  };

  const onDelete = () => {
    onClear();
    onSubmit({
      job_titles: [],
      min_years_experience: '',
      use_boolean_search: false,
      skills: [],
      dev_only_all_apps: false,
      prioritize_current_experience: false,
      industries: [],
    });
    closeDeleteModal();
    onClose();
  };

  const removeSkill = (skillName: string) => {
    setValue(
      'skills',
      skills.filter((s: Skill) => s.name !== skillName),
      { shouldDirty: true }
    );
  };

  const onAddWorkTitle = (titles: string[]) => {
    setValue('job_titles', titles, { shouldDirty: true });
  };

  const onRemoveWorkTitle = (keyword: string) => {
    const remainingItems = [...jobTitles].filter((value) => value !== keyword);
    setValue('job_titles', remainingItems, {
      shouldDirty: remainingItems.length === 0,
    });
  };

  const setSkillValue = (skill: Skill, value: number) => {
    setValue(
      'skills',
      skills.map((s: Skill) =>
        s.name === skill.name ? { ...s, importance: value } : s
      ),
      { shouldDirty: true }
    );
  };

  const currentSkillNames = skills.map((skill: Skill) => skill.name);
  const hasBlockingErrors = validationErrors.some(
    (error) =>
      error.severity === 'block' && currentSkillNames.includes(error.skill)
  );

  const isSaveDisabled =
    isLoading || rankingInProgress || isValidating || hasBlockingErrors;

  const isSaveDisabledDueToPermissions = hasCalibrationSetting
    ? !canEditAndDeleteTalentMatchingCalibration
    : !canCreateCalibrationSetting;

  const isPrimaryButtonDisabled =
    isHistoryTab || isSaveDisabled || isSaveDisabledDueToPermissions;
  const tabIndex = isHistoryTab ? 1 : 0;

  const loadSuggestedPrompt = async () => {
    if (!hasJobDescription) {
      return;
    }

    setSuggestedPromptError(null);
    if (suggestedPrompt) {
      return;
    }

    try {
      const { data, error } = await fetchSuggestedPrompt();

      if (error || !data) {
        setSuggestedPromptError(
          t('phase2.ai_input_section.suggested_prompt_error')
        );
        return;
      }

      setSuggestedPrompt(data);
    } catch {
      setSuggestedPromptError(
        t('phase2.ai_input_section.suggested_prompt_error')
      );
    }
  };

  const onSuggestedPromptSelect = () => {
    if (!suggestedPrompt) {
      return;
    }

    const current = userPrompt || '';
    const appended =
      current.trim().length > 0
        ? `${current}\n${suggestedPrompt}`
        : suggestedPrompt;

    setValue('userPrompt', appended);
    setValue('userPromptMode', 'edit');
  };

  const renderSkills = (isDisabled = false, skillsOverride?: Skill[]) => {
    const skillsToRender = skillsOverride || skills;

    if (isLoading && !isDisabled) {
      return <SkeletonLoader rows={5} columnsPerRow={3} />;
    }

    if (skillsToRender.length === 0) {
      return <Body metadata>{t('calibrate_skills.no_skills')}</Body>;
    }

    return skillsToRender.map((skill: Skill) => (
      <SliderComponent
        key={skill.name}
        value={skill.importance}
        name={skill.name}
        onRemove={isDisabled ? () => undefined : removeSkill}
        onChange={
          isDisabled
            ? () => undefined
            : (value: number) => setSkillValue(skill, value)
        }
        disabled={isDisabled}
        validationError={skillValidationErrors[skill.name]}
      />
    ));
  };

  // Convert structured validation errors to skill-specific errors map
  const skillValidationErrors: Record<string, SkillValidationError> = {};
  validationErrors.forEach((error) => {
    skillValidationErrors[error.skill] = error;
  });

  const handleSubmit = () => {
    if (isHistoryTab) {
      return;
    }

    const parsedMinYearsExperience = parseInt(
      String(minYearsExperience ?? ''),
      10
    );
    const yearsValue = Number.isNaN(parsedMinYearsExperience)
      ? undefined
      : clamp(parsedMinYearsExperience, 0, 50);

    onSubmit({
      job_titles: jobTitles,
      min_years_experience: yearsValue,
      use_boolean_search: includeSimilarRoles,
      skills: skills,
      dev_only_all_apps: devOnlyAllApps,
      prioritize_current_experience: prioritizeCurrentExperience,
      industries: industries.map((industry: Item) => industry.value),
    });
  };

  return (
    <DataEntryModal
      size="extraLarge"
      // @ts-expect-error - Allow overriding modal width until the component exposes a typed prop.
      maxWidth="1280px"
      height="calc(100vh - 80px)"
      isOpen={isOpen}
      onDismiss={handleOnClose}
      ariaLabel={'calibration-modal'}
    >
      <DataEntryModal.Header onClick={handleOnClose}>
        <SectionTitle large>
          {hasExistingCalibration
            ? t('phase2.title_update')
            : t('phase2.title')}
        </SectionTitle>
      </DataEntryModal.Header>

      <CalibrationModalBody
        item
        grow={1}
        sx={{ paddingLeft: 3, paddingRight: 3 }}
      >
        <FullHeightTabs
          index={tabIndex}
          onChange={(index: number) =>
            setActiveTab(index === 1 ? 'history' : 'current')
          }
        >
          <TabListWrapper>
            <TabList aria-label={t('phase2.title')}>
              <Tab index={0}>{t('phase2.current_calibration')}</Tab>
              <Tab index={1}>{t('phase2.history')}</Tab>
            </TabList>
          </TabListWrapper>
          <TabPanels mountingMode={MountingMode.All}>
            <TabPanel index={0}>
              <ColumnsFrame>
                <TwoColumnLayout>
                  <AssistantCard filled>
                    <Stack spacing={1.5}>
                      <AssistantTitle as="div">
                        {t('phase2.ai_input_section.assistant_label')}
                      </AssistantTitle>

                      <AiCallout
                        dismissableKey="calibration-quickstart"
                        headerText={t(
                          'phase2.ai_input_section.quick_start_title'
                        )}
                        bodyText={
                          <SecondaryBody>
                            {t(
                              'phase2.ai_input_section.quick_start_description'
                            )}
                          </SecondaryBody>
                        }
                      />

                      {isCurrentCalibrationTab && (
                        <Stack spacing={1}>
                          <Label>{t('ai_input_section.description')}</Label>
                          <UserPrompt
                            isLoading={isLoading}
                            parseSkillsError={errors.root?.parseSkills?.message}
                          />
                          <SuggestedPromptMenuWrapper>
                            <ExpandableMenu>
                              <SuggestedPromptButton
                                onClick={loadSuggestedPrompt}
                                disabled={
                                  !hasJobDescription ||
                                  isFetchingSuggestedPrompt ||
                                  isLoading
                                }
                                aria-disabled={
                                  !hasJobDescription ||
                                  isFetchingSuggestedPrompt ||
                                  isLoading
                                }
                                title={
                                  !hasJobDescription
                                    ? t(
                                        'phase2.ai_input_section.suggested_prompt_disabled_tooltip'
                                      )
                                    : undefined
                                }
                              >
                                <AddInCircleIcon
                                  size="sm"
                                  color="currentColor"
                                  ariaHidden
                                />
                                <span>
                                  {t(
                                    'phase2.ai_input_section.insert_suggested_prompt'
                                  )}
                                </span>
                                <ArrowDownIcon size="sm" color="currentColor" />
                              </SuggestedPromptButton>
                              <MenuList>
                                <MenuItem disabled onSelect={() => undefined}>
                                  <Body medium>
                                    {t(
                                      'phase2.ai_input_section.suggested_prompt_title'
                                    )}
                                  </Body>
                                </MenuItem>
                                <MenuItem
                                  onSelect={onSuggestedPromptSelect}
                                  disabled={
                                    !suggestedPrompt ||
                                    isFetchingSuggestedPrompt
                                  }
                                >
                                  <Stack spacing={0.25}>
                                    {isFetchingSuggestedPrompt && (
                                      <Stack
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center"
                                      >
                                        <LoadingSpinner size="small" />
                                        <SecondaryBody>
                                          {t(
                                            'phase2.ai_input_section.suggested_prompt_loading'
                                          )}
                                        </SecondaryBody>
                                      </Stack>
                                    )}
                                    {!isFetchingSuggestedPrompt &&
                                      suggestedPrompt && (
                                        <SecondaryBody>
                                          {suggestedPrompt}
                                        </SecondaryBody>
                                      )}
                                    {suggestedPromptError && (
                                      <ErrorText role="alert">
                                        {suggestedPromptError}
                                      </ErrorText>
                                    )}
                                  </Stack>
                                </MenuItem>
                              </MenuList>
                            </ExpandableMenu>
                          </SuggestedPromptMenuWrapper>
                        </Stack>
                      )}
                      <Stack spacing={1}>
                        <Tooltip
                          {...(!hasJobDescription
                            ? {
                                ariaLabel: t(
                                  'phase2.ai_input_section.job_description_disabled_tooltip'
                                ),
                                label: t(
                                  'phase2.ai_input_section.job_description_disabled_tooltip'
                                ),
                              }
                            : { label: null })}
                        >
                          <CheckboxField
                            name="include_job_description"
                            label={t(
                              'phase2.ai_input_section.include_job_description'
                            )}
                            value={'include_job_description'}
                            disabled={!hasJobDescription}
                          />
                        </Tooltip>
                        <Tooltip
                          {...(!hasScorecardAttributes
                            ? {
                                ariaLabel: t(
                                  'phase2.ai_input_section.scorecard_attributes_disabled_tooltip'
                                ),
                                label: t(
                                  'phase2.ai_input_section.scorecard_attributes_disabled_tooltip'
                                ),
                              }
                            : { label: null })}
                        >
                          <CheckboxField
                            name="include_scorecard_attributes"
                            label={t(
                              'phase2.ai_input_section.include_scorecard_attributes'
                            )}
                            value={'include_scorecard_attributes'}
                            disabled={!hasScorecardAttributes}
                          />
                        </Tooltip>
                      </Stack>
                      <Stack spacing={0.5}>
                        <InlineButtonWrapper>
                          <InlineGenerateButton
                            onClick={onGenerateCalibration}
                            disabled={
                              (!userPrompt &&
                                !includeJobDescription &&
                                !includeScorecardAttributes) ||
                              isLoading
                            }
                          >
                            {t('phase2.ai_input_section.generate_calibration')}
                          </InlineGenerateButton>
                        </InlineButtonWrapper>
                        <GenerationDivider>
                          <GenerationNotice metadata>
                            {t(
                              'phase2.ai_input_section.generation_inputs_notice'
                            )}
                          </GenerationNotice>
                        </GenerationDivider>
                      </Stack>
                    </Stack>
                  </AssistantCard>

                  <ColumnDivider aria-hidden />

                  <RightColumn spacing={2}>
                    {isCurrentCalibrationTab && (
                      <Stack spacing={0.25}>
                        <SectionTitle>{t('phase2.callout_title')}</SectionTitle>
                        <SecondaryBody>
                          {hasExistingCalibration
                            ? t('phase2.callout_description_update')
                            : t('phase2.callout_description')}
                        </SecondaryBody>
                      </Stack>
                    )}

                    {isCurrentCalibrationTab && (
                      <>
                        <CalibrationSkillsPanel
                          disabled={false}
                          renderSkills={renderSkills}
                          suggestedSkillsError={suggestedSkillsError}
                        />
                        <CalibrationDetailsPanel
                          disabled={false}
                          jobTitles={jobTitles}
                          onAddWorkTitle={onAddWorkTitle}
                          onRemoveWorkTitle={onRemoveWorkTitle}
                          popperRef={popperRef}
                        />
                      </>
                    )}

                    {isCurrentCalibrationTab && (
                      <Stack
                        spacing={1.5}
                        sx={{
                          alignItems: 'flex-start',
                          paddingBottom: 2,
                        }}
                      >
                        <InlineClearButton
                          onClick={onClear}
                          size="md"
                          disabled={isSaveDisabled}
                        >
                          {t('clear')}
                        </InlineClearButton>
                        <SecondaryBody
                          metadata
                          style={{
                            textAlign: 'left',
                            paddingTop: Tokens.spacing['1'],
                            marginTop: Tokens.spacing['1'],
                            paddingBottom: Tokens.spacing['2'],
                          }}
                        >
                          {t('ai_footer')}
                        </SecondaryBody>
                      </Stack>
                    )}
                  </RightColumn>
                </TwoColumnLayout>
              </ColumnsFrame>
            </TabPanel>
            <TabPanel index={1}>
              {historyItems.length === 0 &&
              !isHistoryLoading &&
              !historyError ? (
                <EmptyHistoryContainer>
                  <EmptyIllustration aria-hidden />
                  <EmptyTitle>
                    {t('phase2.history_tab.empty_state.title')}
                  </EmptyTitle>
                  <EmptySubtitle metadata>
                    {t('phase2.history_tab.empty_state.subtitle')}
                  </EmptySubtitle>
                  <SecondaryButton
                    onClick={() => setActiveTab('current')}
                    size="lg"
                  >
                    {t('phase2.history_tab.empty_state.cta')}
                  </SecondaryButton>
                </EmptyHistoryContainer>
              ) : (
                <HistoryCalibrationTab
                  historyItems={historyItems}
                  selectedHistoryId={selectedHistoryId}
                  onSelectHistory={setSelectedHistoryId}
                  renderSkills={renderSkills}
                  jobTitles={jobTitles}
                  onAddWorkTitle={onAddWorkTitle}
                  onRemoveWorkTitle={onRemoveWorkTitle}
                  popperRef={popperRef}
                  onUseVersion={handleUseVersionClick}
                  onEditVersion={applyHistoryEntryToCurrent}
                  isLoading={isHistoryLoading}
                  errorMessage={
                    historyError ? t('phase2.history_tab.error') : undefined
                  }
                />
              )}
            </TabPanel>
          </TabPanels>
        </FullHeightTabs>
      </CalibrationModalBody>

      <FooterBorderWrapper>
        <ThemeableModal.Footer paddingOverrides={{ paddingTop: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: '100%', marginLeft: 1, marginRight: 1 }}
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              {hasCalibrationSetting &&
                canEditAndDeleteTalentMatchingCalibration &&
                isCurrentCalibrationTab && (
                  <DangerTertiaryButton
                    onClick={openDeleteModal}
                    disabled={isSaveDisabled}
                  >
                    {t('delete')}
                  </DangerTertiaryButton>
                )}
            </Stack>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <SecondaryButton onClick={handleOnClose}>
                {t('cancel')}
              </SecondaryButton>
              {isCurrentCalibrationTab && (
                <Tooltip
                  {...(isSaveDisabledDueToPermissions
                    ? {
                        ariaLabel: t('save_permission_tooltip'),
                        label: t('save_permission_tooltip'),
                      }
                    : { label: null })}
                >
                  <PrimaryButton
                    onClick={handleSubmit}
                    disabled={isPrimaryButtonDisabled}
                  >
                    {t('save')}
                  </PrimaryButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </ThemeableModal.Footer>
      </FooterBorderWrapper>

      <ConfirmationModal
        isOpen={isUseVersionModalOpen}
        onDismiss={dismissUseVersionModal}
        ariaLabel="Use calibration version"
      >
        <ConfirmationModal.Header>
          {t('phase2.history_tab.use_version_modal.title')}
        </ConfirmationModal.Header>
        <ConfirmationModal.Body>
          <Body>{t('phase2.history_tab.use_version_modal.description')}</Body>
        </ConfirmationModal.Body>
        <ConfirmationModal.Footer>
          <SecondaryButton onClick={dismissUseVersionModal}>
            {t('phase2.history_tab.use_version_modal.cancel')}
          </SecondaryButton>
          <PrimaryButton onClick={() => applyHistoryEntryToCurrent()}>
            {t('phase2.history_tab.use_version_modal.confirm')}
          </PrimaryButton>
        </ConfirmationModal.Footer>
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={isExitWithoutSavingModalOpen}
        onDismiss={closeExitWithoutSavingModal}
        ariaLabel={t('phase2.exit_modal.title')}
      >
        <ConfirmationModal.Header>
          {t('phase2.exit_modal.title')}
        </ConfirmationModal.Header>
        <ConfirmationModal.Body>
          <Body>{t('phase2.exit_modal.description')}</Body>
        </ConfirmationModal.Body>
        <ConfirmationModal.Footer>
          <SecondaryButton onClick={closeExitWithoutSavingModal}>
            {t('phase2.exit_modal.stay')}
          </SecondaryButton>
          <PrimaryButton onClick={closeAndReset}>
            {t('phase2.exit_modal.confirm')}
          </PrimaryButton>
        </ConfirmationModal.Footer>
      </ConfirmationModal>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        close={closeDeleteModal}
        onDelete={onDelete}
      />
    </DataEntryModal>
  );
};

export default CalibrationForm;
